import {
  AccountData,
  Event,
  TicketV2,
  RequestStatus,
  SubSelectionItem,
  SubSelectionType,
  SubSelectionStatusId,
  FormResponseV2,
  SelectRecipientGroupType,
  FollowerStatus,
  EventType,
} from "@markit/common.types";
import FlatList from "flatlist-react/lib";
import filter from "lodash.filter";
import { useState, useMemo, useEffect, useCallback } from "react";
import { MassTextsPersonItem } from "../../Items/MassTextsPersonItem";
import { RenderedEventMassTextsModal } from "../../../../LandingPage/RenderedEvents";
import { useLoadUserTicketList } from "../../../../../hooks/useLoadUserTicketList";
import { Colors } from "../../../../../utils/colors";
import { fetchAllEventTickets } from "../../../../../utils/eventUtils/eventUtils";
import { Icon } from "@iconify/react";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../../../redux/slices/accountSlice";
import {
  addOrFilterCategories,
  addOrFilterRecipients,
  eventHasRequestTickets,
  isAllAttendeesCategorySelected,
  isAllPeopleCategorySelected,
  isEventTicketsPaid,
  isExternalEventbrite,
  isExternalShowclix,
  isItemGroupSelected,
  isProductSpecificType,
  uniqueVals,
} from "@markit/common.utils";
import { EmptySearchState } from "../../../../EmptyStates/EmptySearchState";
import { RecipientsAddAllItem } from "../../Items/RecipientsAddAllItem";
import { getEventFormResponses } from "../../../../../utils/eventUtils/formUtils";
import SelectRecipientItemsFormQuestion from "../SelectCollectedData/SelectRecipientsItemsFormQuestion";
import SearchBoxContainer from "../../../../Containers/SearchBoxContainer";
import { MassTextsActionItem } from "../../Items/MassTextsActionItem";
import ListTabs from "../../../../DropdownsAndTabs/ListTabs";
import { SelectRecipientsSharedProps } from "../SelectRecipientsScreens";
import { RecipientsAddAllItemDropdown } from "../../Items/RecipientsAddAllItemDropdown";
import { HorizontalDivider } from "../../../../Dividers/HorizontalDivider";
import { fetchAttendeesPerTypeMap } from "../../../../../utils/textingUtils";
import useAsyncOnMount from "../../../../../hooks/useAsyncEffectOnMount";
import VirtuosoList from "../../../../Lists/VirtuosoList";

type SelectRecipientsEventProps = SelectRecipientsSharedProps & {
  selectedType: SelectRecipientGroupType;
};

const SelectRecipientsEvent = (props: SelectRecipientsEventProps) => {
  const {
    unsavedRecipients,
    setUnsavedRecipients,
    unsavedCategories,
    setUnsavedCategories,
    selectedType,
    excludingMode,
    showCategories,
    setProfileSelected,
    displayAllFollowers,
  } = props;
  const { savedQuestions, followersData } =
    useSelector(getAccountState).account;

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [listView, setListView] = useState(0);
  const [ticketTypeAttendees, setTicketTypeAttendees] = useState<
    Map<string, string[]>
  >(new Map<string, string[]>());
  const [formResponses, setFormResponses] = useState<FormResponseV2[]>([]);
  const [allPeopleTickets, setAllPeopleTickets] = useState<TicketV2[]>([]);
  const [showStatusSubOptions, setShowStatusSubOptions] = useState(true);

  const styles = { optionSubtext: { fontSize: 12, marginTop: 3 } };

  const addAllItemSubtext = useMemo(
    () => (showCategories ? undefined : "People"),
    [showCategories]
  );

  const selectedEvent = useMemo(
    () =>
      (selectedType.selectedItem as unknown as Event)
        ? (selectedType.selectedItem as unknown as Event)
        : undefined,
    [selectedType.selectedItem]
  );

  const isFormType = useMemo(
    () => selectedEvent && selectedEvent.eventType === EventType.GENERIC_LINK,
    [selectedEvent]
  );

  const isEventbriteExternal = useMemo(
    () => selectedEvent && isExternalEventbrite(selectedEvent.eventType),
    [selectedEvent]
  );

  // Either Showclix or Linked Events with Data Collection
  const isExternalLinkedEvent = useMemo(
    () =>
      selectedEvent &&
      (isExternalShowclix(selectedEvent.eventType) ||
        selectedEvent.eventType === EventType.EXTERNAL_DATA),
    [selectedEvent]
  );

  const isProductType = useMemo(
    () => selectedEvent && isProductSpecificType(selectedEvent.eventType),
    [selectedEvent]
  );

  const showRefundedTab = useMemo(
    () => selectedEvent && isEventTicketsPaid(selectedEvent),
    [selectedEvent]
  );

  // filter down by the visible attendees (ie. only subscribed or not)
  const allVisiblePeopleTickets = useMemo(
    () =>
      displayAllFollowers
        ? allPeopleTickets
        : allPeopleTickets.filter(
            (tickets) =>
              followersData.find((follower) => follower.uid === tickets.uid)
                ?.status === FollowerStatus.SUBSCRIBED
          ),
    [allPeopleTickets, displayAllFollowers, followersData]
  );

  // filter down by the visible form responses (ie. only subscribed or not)
  const visibleFormResponses = useMemo(
    () =>
      displayAllFollowers
        ? formResponses
        : formResponses.filter(
            (form) =>
              followersData.find((follower) => follower.uid === form.uid)
                ?.status === FollowerStatus.SUBSCRIBED
          ),
    [displayAllFollowers, followersData, formResponses]
  );

  // filter down by the visible attendees based on ticket type (ie. only subscribed or not)
  const visibleTicketTypeAttendees = useMemo(() => {
    if (displayAllFollowers) {
      return ticketTypeAttendees;
    }
    // Create a new map to store the filtered results
    const subscribedAttendeesPerTicketType = new Map();

    // Iterate over the map
    ticketTypeAttendees.forEach((valueArray, key) => {
      const filteredArray = valueArray.filter(
        (uid) =>
          followersData.find((follower) => follower.uid === uid)?.status ===
          FollowerStatus.SUBSCRIBED
      );

      // Only add the key to the new map if the filtered array is not empty
      if (filteredArray.length > 0) {
        subscribedAttendeesPerTicketType.set(key, filteredArray);
      }
    });
    return subscribedAttendeesPerTicketType;
  }, [displayAllFollowers, followersData, ticketTypeAttendees]);

  const getUniqueTicketUids = (tickets: TicketV2[]) => {
    return uniqueVals(tickets, (ticket: TicketV2) => ticket.uid).map(
      (ticket) => ticket.uid
    );
  };

  // All people ticket users (only accepted tickets)
  const allPeopleTicketUsers = useMemo(
    () =>
      getUniqueTicketUids(
        allVisiblePeopleTickets.filter(
          (ticket) => ticket.requestStatus !== RequestStatus.REFUNDED
        )
      ),
    [allVisiblePeopleTickets]
  );

  // All tickets that are accepted status
  const allAcceptedAttendeeTickets = useMemo(
    () =>
      uniqueVals(
        allVisiblePeopleTickets.filter(
          (ticket) => ticket.requestStatus === RequestStatus.ACCEPTED
        ),
        (ticket: TicketV2) => ticket.uid
      ),
    [allVisiblePeopleTickets]
  );

  const allAcceptedAttendeeIds = useMemo(
    () => getUniqueTicketUids(allAcceptedAttendeeTickets),
    [allAcceptedAttendeeTickets]
  );

  // All people tickets users that are in pending request status
  const allRequestedAttendeeIds = useMemo(
    () =>
      getUniqueTicketUids(
        allVisiblePeopleTickets.filter(
          (ticket) => ticket.requestStatus === RequestStatus.PENDING
        )
      ),
    [allVisiblePeopleTickets]
  );

  // All people tickets users that are in refunded status
  const allRefundedAttendeeIds = useMemo(
    () =>
      getUniqueTicketUids(
        allVisiblePeopleTickets.filter(
          (ticket) => ticket.requestStatus === RequestStatus.REFUNDED
        )
      ),
    [allVisiblePeopleTickets]
  );

  // To be used for isItemGroupSelected
  const totalListToShow = useCallback(
    (recipientList: string[]) => {
      return showCategories ? unsavedCategories : recipientList;
    },
    [showCategories, unsavedCategories]
  );

  const eventFormQuestions = useMemo(
    () =>
      selectedEvent
        ? savedQuestions.filter(
            (question) => question.eventIds.includes(selectedEvent.id),
            []
          )
        : [],
    [savedQuestions, selectedEvent]
  );

  const iconColor = useMemo(
    () =>
      excludingMode
        ? Colors.RED3
        : showCategories
        ? Colors.PURPLE7
        : Colors.BLUE5,
    [excludingMode, showCategories]
  );

  const {
    isFinished: isAttendeeFinished,
    isLoading: isAttendeeLoading,
    fetchedUserData: fullAttendeeAccountData,
    loadTicketUsers: loadAttendeeUsers,
    loadMoreTicketUsers: loadMoreAttendeeUsers,
    loadSearchResultsThrottled,
  } = useLoadUserTicketList({
    event: selectedEvent,
    followerStatus: displayAllFollowers ? undefined : FollowerStatus.SUBSCRIBED,
    windowSize: 15,
    ticketType: RequestStatus.ACCEPTED,
  });

  const {
    isFinished: isRefundedFinished,
    isLoading: isRefundedLoading,
    fetchedUserData: fullRefundedAccountData,
    loadTicketUsers: loadRefundedUsers,
    loadMoreTicketUsers: loadMoreRefundedUsers,
    loadSearchResultsThrottled: loadRefundedSearchResultsThrottled,
  } = useLoadUserTicketList({
    event: selectedEvent,
    followerStatus: displayAllFollowers ? undefined : FollowerStatus.SUBSCRIBED,
    windowSize: 20,
    ticketType: RequestStatus.REFUNDED,
  });

  useAsyncOnMount(async () => {
    if (selectedEvent) {
      const [attendeesPerTicketType, eventFormResponses, peopleTickets] =
        await Promise.all([
          fetchAttendeesPerTypeMap(selectedEvent),
          getEventFormResponses(selectedEvent.id),
          fetchAllEventTickets(selectedEvent.id),
        ]);
      setTicketTypeAttendees(attendeesPerTicketType); // for by ticket type
      setFormResponses(eventFormResponses); // for by form response
      setAllPeopleTickets(peopleTickets);
    }
    setLoading(false);
  });

  // To handle pre-setting the focused tab when selecting by event
  useEffect(() => {
    setListView(selectedType.eventTabValue);
  }, [selectedType.eventTabValue]);

  useAsyncOnMount(async () => {
    await Promise.all([loadAttendeeUsers(), loadRefundedUsers()]);
  });

  const containsUser = useCallback((item: AccountData, query: string) => {
    return item.fullName.toLowerCase().includes(query);
  }, []);

  const handleSearch = (text: string) => {
    if (text !== "") {
      if (listView === 4) {
        loadRefundedSearchResultsThrottled(text.toLowerCase());
      } else {
        loadSearchResultsThrottled(text.toLowerCase());
      }
    }
    setSearchTerm(text);
  };

  const attendeeTicketsToShow: AccountData[] = useMemo(() => {
    let searchResults: AccountData[] = fullAttendeeAccountData;
    if (searchTerm !== "") {
      searchResults = filter(searchResults, (user: AccountData) => {
        return containsUser(user, searchTerm);
      });
    }
    return searchResults;
  }, [fullAttendeeAccountData, searchTerm, containsUser]);

  const refundedTicketsToShow: AccountData[] = useMemo(() => {
    let searchResults: AccountData[] = fullRefundedAccountData;
    if (searchTerm !== "") {
      searchResults = filter(searchResults, (user: AccountData) => {
        return containsUser(user, searchTerm);
      });
    }
    return searchResults;
  }, [fullRefundedAccountData, searchTerm, containsUser]);

  const getCheckInRecipientIds = useCallback(
    (isScanned: boolean) => {
      return uniqueVals(
        allAcceptedAttendeeTickets
          .filter((ticket) => (isScanned ? ticket.scanned : !ticket.scanned))
          .map((ticket) => ticket.uid)
      );
    },
    [allAcceptedAttendeeTickets]
  );

  const selectAllOnPress = useCallback(() => {
    if (showCategories) {
      const allPeopleCategory: SubSelectionItem = {
        type: SubSelectionType.ALL_PEOPLE,
        id: "",
        formOption: "",
      };
      addOrFilterCategories(
        isAllPeopleCategorySelected(unsavedCategories) ? unsavedCategories : [],
        setUnsavedCategories,
        allPeopleCategory
      );
    } else {
      addOrFilterRecipients(
        unsavedRecipients,
        setUnsavedRecipients,
        allPeopleTicketUsers
      );
    }
  }, [
    showCategories,
    unsavedCategories,
    setUnsavedCategories,
    unsavedRecipients,
    setUnsavedRecipients,
    allPeopleTicketUsers,
  ]);

  const onSelectAllStatusUsers = useCallback(
    async (status: SubSelectionStatusId) => {
      if (showCategories) {
        const statusCategory: SubSelectionItem = {
          type: SubSelectionType.STATUS,
          id: status,
          formOption: "",
        };
        if (status === SubSelectionStatusId.ATTENDEES) {
          const foundNonAttendeeCategories = unsavedCategories.filter(
            (category) =>
              category.id !== SubSelectionStatusId.CHECKED_IN &&
              category.id !== SubSelectionStatusId.NOT_CHECKED_IN
          );
          addOrFilterCategories(
            isAllAttendeesCategorySelected(unsavedCategories)
              ? unsavedCategories
              : foundNonAttendeeCategories,
            setUnsavedCategories,
            statusCategory
          );
        } else {
          addOrFilterCategories(
            unsavedCategories,
            setUnsavedCategories,
            statusCategory
          );
        }
      } else {
        if (
          status === SubSelectionStatusId.CHECKED_IN ||
          status === SubSelectionStatusId.NOT_CHECKED_IN
        ) {
          const checkInRecipients = getCheckInRecipientIds(
            status === SubSelectionStatusId.CHECKED_IN
          );
          addOrFilterRecipients(
            unsavedRecipients,
            setUnsavedRecipients,
            checkInRecipients
          );
        } else {
          addOrFilterRecipients(
            unsavedRecipients,
            setUnsavedRecipients,
            status === SubSelectionStatusId.ATTENDEES
              ? allAcceptedAttendeeIds
              : allRequestedAttendeeIds
          );
        }
      }
    },
    [
      showCategories,
      unsavedCategories,
      setUnsavedCategories,
      getCheckInRecipientIds,
      unsavedRecipients,
      setUnsavedRecipients,
      allAcceptedAttendeeIds,
      allRequestedAttendeeIds,
    ]
  );

  const onSelectAllTicketUsers = useCallback(
    async (customTicketId: string) => {
      // Custom Selection Categories
      if (showCategories) {
        const newSubSelectionItem: SubSelectionItem = {
          type: SubSelectionType.TICKET_TYPE,
          id: customTicketId,
          formOption: "",
        };
        addOrFilterCategories(
          unsavedCategories,
          setUnsavedCategories,
          newSubSelectionItem
        );
      } else {
        const foundAttendees = visibleTicketTypeAttendees.get(customTicketId);
        if (foundAttendees) {
          addOrFilterRecipients(
            unsavedRecipients,
            setUnsavedRecipients,
            foundAttendees
          );
        }
      }
    },
    [
      showCategories,
      unsavedCategories,
      setUnsavedCategories,
      visibleTicketTypeAttendees,
      unsavedRecipients,
      setUnsavedRecipients,
    ]
  );

  const selectAllRefundedOnPress = useCallback(() => {
    if (showCategories) {
      const allRefundedCategory: SubSelectionItem = {
        type: SubSelectionType.REFUNDED,
        id: "",
        formOption: "",
      };
      addOrFilterCategories(
        unsavedCategories,
        setUnsavedCategories,
        allRefundedCategory
      );
    } else {
      addOrFilterRecipients(
        unsavedRecipients,
        setUnsavedRecipients,
        allRefundedAttendeeIds
      );
    }
  }, [
    showCategories,
    unsavedCategories,
    setUnsavedCategories,
    unsavedRecipients,
    setUnsavedRecipients,
    allRefundedAttendeeIds,
  ]);

  const renderHeader = useMemo(
    () => (
      <div>
        {!showCategories && (isEventbriteExternal || isExternalLinkedEvent) ? (
          <>
            <div className="ColumnNormal" style={{ gap: 4, paddingTop: 7 }}>
              <span className="bodyMedium">
                Only showing textable contacts for this event.
              </span>
              <span className="smallBodySubtext">
                Attendees with no phone number are not textable.
              </span>
            </div>
            <HorizontalDivider altMargin={10} />
          </>
        ) : null}
        <MassTextsActionItem
          title={showCategories ? "All People" : "Select All People"}
          icon={
            <Icon icon="ion:people" height={32} style={{ color: iconColor }} />
          }
          onPress={selectAllOnPress}
          subtext={
            loading
              ? ""
              : showCategories
              ? isFormType
                ? "All Submissions"
                : `All ${
                    isProductType ? "Orders" : "Attendees"
                  }, Requested, etc.`
              : `${allPeopleTicketUsers.length} ${
                  allPeopleTicketUsers.length !== 1 ? "People" : "Person"
                }`
          }
          isCheckSelected={isItemGroupSelected(
            totalListToShow(allPeopleTicketUsers),
            unsavedRecipients,
            showCategories,
            {
              type: SubSelectionType.ALL_PEOPLE,
              id: "",
              formOption: "",
            }
          )}
          checkColor={iconColor}
          disabled={showCategories && excludingMode}
        />
        <HorizontalDivider altMargin={10} />
      </div>
    ),
    [
      showCategories,
      isEventbriteExternal,
      isExternalLinkedEvent,
      iconColor,
      selectAllOnPress,
      loading,
      isFormType,
      isProductType,
      allPeopleTicketUsers,
      totalListToShow,
      unsavedRecipients,
      excludingMode,
    ]
  );

  const renderRefundedHeader = useMemo(
    () => (
      <div>
        <MassTextsActionItem
          title={showCategories ? "All Refunded" : "Select All Refunded"}
          icon={
            <Icon icon="ion:receipt" height={32} style={{ color: iconColor }} />
          }
          onPress={selectAllRefundedOnPress}
          subtext={
            loading
              ? ""
              : showCategories
              ? undefined
              : `${allRefundedAttendeeIds.length} People`
          }
          isCheckSelected={isItemGroupSelected(
            totalListToShow(allRefundedAttendeeIds),
            unsavedRecipients,
            showCategories,
            {
              type: SubSelectionType.REFUNDED,
              id: "",
              formOption: "",
            }
          )}
          checkColor={iconColor}
        />
        <HorizontalDivider altMargin={10} />
      </div>
    ),
    [
      showCategories,
      iconColor,
      selectAllRefundedOnPress,
      loading,
      allRefundedAttendeeIds,
      totalListToShow,
      unsavedRecipients,
    ]
  );

  const renderPeopleTab = useMemo(
    () =>
      showCategories ? (
        renderHeader
      ) : !showCategories ? (
        <VirtuosoList
          items={attendeeTicketsToShow}
          loadMoreOnPress={loadMoreAttendeeUsers}
          renderItem={(item) => {
            return (
              <MassTextsPersonItem
                user={item}
                unsavedPeople={unsavedRecipients}
                setUnsavedPeople={setUnsavedRecipients}
                excludingMode={excludingMode}
                setProfileSelected={setProfileSelected}
              />
            );
          }}
          isLoading={isAttendeeLoading}
          isFinished={isAttendeeFinished}
          searchTerm={searchTerm}
          renderNonSearchEmptyView={
            <EmptySearchState mainText="No people to show" />
          }
          renderHeader={renderHeader}
        />
      ) : null,
    [
      attendeeTicketsToShow,
      excludingMode,
      isAttendeeFinished,
      isAttendeeLoading,
      loadMoreAttendeeUsers,
      renderHeader,
      searchTerm,
      setProfileSelected,
      setUnsavedRecipients,
      showCategories,
      unsavedRecipients,
    ]
  );

  const renderStatusTab = useMemo(
    () => (
      <>
        <RecipientsAddAllItemDropdown
          mainText="All Attendees"
          subText={
            !showCategories ? (
              <span style={styles.optionSubtext}>
                {allAcceptedAttendeeIds.length} {addAllItemSubtext}
              </span>
            ) : undefined
          }
          excludingMode={excludingMode}
          isAllSelected={isItemGroupSelected(
            totalListToShow(allAcceptedAttendeeIds),
            unsavedRecipients,
            showCategories,

            {
              type: SubSelectionType.STATUS,
              id: SubSelectionStatusId.ATTENDEES,
              formOption: "",
            }
          )}
          selectRecipients={allAcceptedAttendeeIds}
          onPress={() => setShowStatusSubOptions(!showStatusSubOptions)}
          onCheckPress={() =>
            onSelectAllStatusUsers(SubSelectionStatusId.ATTENDEES)
          }
          showDropdown={showStatusSubOptions}
          isCategories={showCategories}
          disabled={
            isAllPeopleCategorySelected(unsavedCategories) ||
            (showCategories && excludingMode)
          }
        />
        {showStatusSubOptions ? (
          <>
            <RecipientsAddAllItem
              label="Checked In"
              sublabel={addAllItemSubtext}
              selectRecipients={getCheckInRecipientIds(true)}
              isAllSelected={
                isItemGroupSelected(
                  totalListToShow(getCheckInRecipientIds(true)),
                  unsavedRecipients,
                  showCategories,
                  {
                    type: SubSelectionType.STATUS,
                    id: SubSelectionStatusId.CHECKED_IN,
                    formOption: "",
                  }
                ) || isAllAttendeesCategorySelected(unsavedCategories)
              }
              onPress={() =>
                onSelectAllStatusUsers(SubSelectionStatusId.CHECKED_IN)
              }
              excludingMode={excludingMode}
              isCategories={showCategories}
              disabled={
                isAllPeopleCategorySelected(unsavedCategories) ||
                isAllAttendeesCategorySelected(unsavedCategories)
              }
              noDivider
            />
            <RecipientsAddAllItem
              label="Not Checked In"
              sublabel={addAllItemSubtext}
              excludingMode={excludingMode}
              isAllSelected={
                isItemGroupSelected(
                  totalListToShow(getCheckInRecipientIds(false)),
                  unsavedRecipients,
                  showCategories,
                  {
                    type: SubSelectionType.STATUS,
                    id: SubSelectionStatusId.NOT_CHECKED_IN,
                    formOption: "",
                  }
                ) || isAllAttendeesCategorySelected(unsavedCategories)
              }
              selectRecipients={getCheckInRecipientIds(false)}
              onPress={() => {
                onSelectAllStatusUsers(SubSelectionStatusId.NOT_CHECKED_IN);
              }}
              isCategories={showCategories}
              disabled={
                isAllPeopleCategorySelected(unsavedCategories) ||
                isAllAttendeesCategorySelected(unsavedCategories)
              }
              noDivider
            />
          </>
        ) : null}
        <HorizontalDivider altMargin={10} />
        {selectedEvent && eventHasRequestTickets(selectedEvent) ? (
          <RecipientsAddAllItem
            label="Requested"
            sublabel={addAllItemSubtext}
            excludingMode={excludingMode}
            isAllSelected={isItemGroupSelected(
              totalListToShow(allRequestedAttendeeIds),
              unsavedRecipients,
              showCategories,
              {
                type: SubSelectionType.STATUS,
                id: SubSelectionStatusId.REQUESTED,
                formOption: "",
              }
            )}
            selectRecipients={allRequestedAttendeeIds}
            onPress={() =>
              onSelectAllStatusUsers(SubSelectionStatusId.REQUESTED)
            }
            isCategories={showCategories}
            disabled={isAllPeopleCategorySelected(unsavedCategories)}
          />
        ) : null}
      </>
    ),
    [
      addAllItemSubtext,
      allAcceptedAttendeeIds,
      allRequestedAttendeeIds,
      excludingMode,
      getCheckInRecipientIds,
      onSelectAllStatusUsers,
      selectedEvent,
      showCategories,
      showStatusSubOptions,
      styles.optionSubtext,
      totalListToShow,
      unsavedCategories,
      unsavedRecipients,
    ]
  );

  const renderResponsesTab = useMemo(
    () => (
      <div className="HideScrollbar AllowScroll" style={{ paddingBottom: 200 }}>
        <FlatList
          list={eventFormQuestions}
          renderItem={(formQuestion) => (
            <SelectRecipientItemsFormQuestion
              formQuestion={formQuestion}
              formQuestionResponses={visibleFormResponses}
              recipients={unsavedRecipients}
              setRecipients={setUnsavedRecipients}
              excludingMode={excludingMode}
              categoriesToShow={unsavedCategories}
              setCategoriesToShow={setUnsavedCategories}
              showCategories={showCategories}
            />
          )}
          renderWhenEmpty={() => (
            <EmptySearchState mainText="No form questions to show" />
          )}
        />
      </div>
    ),
    [
      eventFormQuestions,
      excludingMode,
      setUnsavedCategories,
      setUnsavedRecipients,
      showCategories,
      visibleFormResponses,
      unsavedCategories,
      unsavedRecipients,
    ]
  );

  const renderTicketsTab = useMemo(
    () => (
      <div className="HideScrollbar AllowScroll" style={{ paddingBottom: 200 }}>
        <FlatList
          list={selectedEvent!.customTickets}
          renderItem={(ticket) => (
            <RecipientsAddAllItem
              label={ticket.label}
              sublabel={addAllItemSubtext}
              selectRecipients={visibleTicketTypeAttendees.get(ticket.id) ?? []}
              isAllSelected={isItemGroupSelected(
                totalListToShow(
                  visibleTicketTypeAttendees.get(ticket.id) || []
                ),
                unsavedRecipients,
                showCategories,
                {
                  type: SubSelectionType.TICKET_TYPE,
                  id: ticket.id,
                  formOption: "",
                }
              )}
              onPress={() => onSelectAllTicketUsers(ticket.id)}
              isCategories={showCategories}
              excludingMode={excludingMode}
              disabled={isAllPeopleCategorySelected(unsavedCategories)}
            />
          )}
          renderWhenEmpty={<EmptySearchState mainText="No tickets to show" />}
        />
      </div>
    ),
    [
      addAllItemSubtext,
      excludingMode,
      onSelectAllTicketUsers,
      selectedEvent,
      showCategories,
      visibleTicketTypeAttendees,
      totalListToShow,
      unsavedCategories,
      unsavedRecipients,
    ]
  );

  const renderRefundedTicketsTab = useMemo(
    () =>
      showCategories ? (
        renderRefundedHeader
      ) : !showCategories ? (
        <VirtuosoList
          items={refundedTicketsToShow}
          loadMoreOnPress={loadMoreRefundedUsers}
          renderItem={(item) => {
            return (
              <MassTextsPersonItem
                user={item}
                unsavedPeople={unsavedRecipients}
                setUnsavedPeople={setUnsavedRecipients}
                excludingMode={excludingMode}
                setProfileSelected={setProfileSelected}
              />
            );
          }}
          isLoading={isRefundedLoading}
          isFinished={isRefundedFinished}
          searchTerm={searchTerm}
          renderNonSearchEmptyView={
            <EmptySearchState mainText="No refunded items to show" />
          }
          renderHeader={renderRefundedHeader}
        />
      ) : null,
    [
      refundedTicketsToShow,
      excludingMode,
      isRefundedFinished,
      isRefundedLoading,
      loadMoreRefundedUsers,
      renderRefundedHeader,
      searchTerm,
      setProfileSelected,
      setUnsavedRecipients,
      showCategories,
      unsavedRecipients,
    ]
  );

  const peopleTabNumber = showCategories ? -1 : allPeopleTicketUsers.length;

  const { tabLabels, tabNumbers } = useMemo(() => {
    if (isExternalLinkedEvent) {
      return {
        tabLabels: ["People", "Responses", "Ticket"],
        tabNumbers: [peopleTabNumber, -1, -1],
      };
    }

    if (isEventbriteExternal) {
      return {
        tabLabels: ["People", "Ticket"],
        tabNumbers: [allPeopleTicketUsers.length, -1],
      };
    }

    if (isFormType) {
      return {
        tabLabels: ["People", "Responses"],
        tabNumbers: [-1, -1],
      };
    }

    if (isProductType) {
      return {
        tabLabels:
          showCategories || !showRefundedTab
            ? ["People", "Responses", "Order Option"]
            : ["People", "Responses", "Order Option", "Refunded"],
        tabNumbers: [peopleTabNumber, -1, -1, -1],
      };
    }

    return {
      tabLabels: showRefundedTab
        ? ["People", "Status", "Responses", "Ticket", "Refunded"]
        : ["People", "Status", "Responses", "Ticket"],
      tabNumbers: [peopleTabNumber, -1, -1, -1, -1],
    };
  }, [
    allPeopleTicketUsers.length,
    isEventbriteExternal,
    isExternalLinkedEvent,
    isFormType,
    isProductType,
    peopleTabNumber,
    showCategories,
    showRefundedTab,
  ]);

  return (
    <>
      <div
        style={{ backgroundColor: Colors.GRAY6, padding: 10, borderRadius: 12 }}
      >
        <RenderedEventMassTextsModal event={selectedEvent!} disabled />
      </div>
      <SearchBoxContainer
        value={searchTerm}
        placeholder="Search People"
        onChange={(e) => handleSearch(e.target.value)}
      />
      <ListTabs
        tabLabels={tabLabels}
        tabNumbers={tabNumbers}
        loading={loading}
        selectedValue={listView}
        onChange={setListView}
        containerStyles={{ paddingBlock: 12 }}
        altColor={iconColor}
      />
      {isExternalLinkedEvent ? (
        <>
          {listView === 0 ? renderPeopleTab : null}
          {listView === 1 ? renderResponsesTab : null}
          {listView === 2 && selectedEvent ? renderTicketsTab : null}
        </>
      ) : isEventbriteExternal ? (
        <>
          {listView === 0 ? renderPeopleTab : null}
          {listView === 1 && selectedEvent ? renderTicketsTab : null}
        </>
      ) : isFormType ? (
        <>
          {listView === 0 ? renderPeopleTab : null}
          {listView === 1 ? renderResponsesTab : null}
        </>
      ) : isProductType ? (
        <>
          {listView === 0 ? renderPeopleTab : null}
          {listView === 1 ? renderResponsesTab : null}
          {listView === 2 ? renderTicketsTab : null}
          {listView === 3 ? renderRefundedTicketsTab : null}
        </>
      ) : (
        <>
          {listView === 0 ? renderPeopleTab : null}
          {listView === 1 ? renderStatusTab : null}
          {listView === 2 ? renderResponsesTab : null}
          {listView === 3 && selectedEvent ? renderTicketsTab : null}
          {listView === 4 ? renderRefundedTicketsTab : null}
        </>
      )}
    </>
  );
};

export default SelectRecipientsEvent;
